import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  StarIcon,
  ClipboardDocumentListIcon,
  DocumentArrowUpIcon,
  DocumentIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  DocumentMagnifyingGlassIcon,
  PhotoIcon,
  SwatchIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-toastify';
import templateService from '../../services/fileUploadApi';
import EditorCampiTemplate from './EditorCampiTemplate';

const GestoreTemplatePreventivi = ({ isOpen, onClose, agenzia }) => {
  // Stati esistenti per template
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [editorTemplate, setEditorTemplate] = useState(null);

  // Nuovi stati per l'analisi dei campi PDF
  const [campiPdf, setCampiPdf] = useState([]);
  const [analizzandoPdf, setAnalizzandoPdf] = useState(false);
  const [showCampiPreview, setShowCampiPreview] = useState(false);

  // NUOVO: Stati per gestione tab
  const [activeTab, setActiveTab] = useState('branding');

  // NUOVO: Stati per branding
  const [brandingData, setBrandingData] = useState({
    logo: null,
    colorePrincipale: '#3B82F6',
    logoFile: null
  });
  const [loadingBranding, setLoadingBranding] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Form state semplificato per template
  const [formData, setFormData] = useState({
    nomeTemplate: '',
    tipoTemplate: 'con_intestazione_cliente',
    isPrimario: false,
    file: null
  });

  useEffect(() => {
    if (isOpen && agenzia) {
      caricaTemplates();
      caricaBranding();
    }
  }, [isOpen, agenzia]);

  // NUOVO: Funzione per caricare i dati di branding
  const caricaBranding = async () => {
    try {
      setLoadingBranding(true);
      
      const response = await fetch(`/api/branding/${agenzia.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setBrandingData({
          logo: data.data.logoUrl,
          colorePrincipale: data.data.colorePrincipale || '#3B82F6',
          logoFile: null
        });
      } else {
        throw new Error(data.error || 'Errore nel caricamento del branding');
      }
    } catch (error) {
      console.error('Errore caricamento branding:', error);
      toast.error('Errore nel caricamento del branding');
      
      // Valori di default in caso di errore
      setBrandingData({
        logo: null,
        colorePrincipale: '#3B82F6',
        logoFile: null
      });
    } finally {
      setLoadingBranding(false);
    }
  };

  // NUOVO: Gestione upload logo
  const handleLogoChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validazione file
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Sono accettati solo file immagine (JPG, PNG, GIF, SVG)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error('Il file non può superare i 5MB');
      return;
    }

    // Anteprima locale
    const reader = new FileReader();
    reader.onload = (e) => {
      setBrandingData(prev => ({
        ...prev,
        logo: e.target.result,
        logoFile: file
      }));
    };
    reader.readAsDataURL(file);
  };

  // NUOVO: Salvataggio branding
  const salvaBranding = async () => {
    try {
      setUploadingLogo(true);
      
      // Se c'è un nuovo logo da caricare, lo carica prima
      if (brandingData.logoFile) {
        const formData = new FormData();
        formData.append('logo', brandingData.logoFile);
        
        const logoResponse = await fetch(`/api/branding/${agenzia.id}/logo`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        });
        
        const logoData = await logoResponse.json();
        if (!logoData.success) {
          throw new Error(logoData.error || 'Errore nel caricamento del logo');
        }
      }
      
      // Salva le impostazioni del branding (colore, ecc.)
      const response = await fetch(`/api/branding/${agenzia.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          colorePrincipale: brandingData.colorePrincipale
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Branding salvato con successo!');
        // Ricarica i dati per aggiornare l'interfaccia
        await caricaBranding();
      } else {
        throw new Error(data.error || 'Errore nel salvataggio del branding');
      }
      
    } catch (error) {
      console.error('Errore salvataggio branding:', error);
      toast.error(`Errore nel salvataggio del branding: ${error.message}`);
    } finally {
      setUploadingLogo(false);
    }
  };

  // NUOVO: Reset logo
  const rimuoviLogo = async () => {
    try {
      setUploadingLogo(true);
      
      // Rimuovi logo dal server
      const response = await fetch(`/api/branding/${agenzia.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          rimuoviLogo: true
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setBrandingData(prev => ({
          ...prev,
          logo: null,
          logoFile: null
        }));
        toast.success('Logo rimosso con successo');
      } else {
        throw new Error(data.error || 'Errore nella rimozione del logo');
      }
      
    } catch (error) {
      console.error('Errore rimozione logo:', error);
      toast.error(`Errore nella rimozione del logo: ${error.message}`);
    } finally {
      setUploadingLogo(false);
    }
  };

  const caricaTemplates = async () => {
    try {
      setLoading(true);
      const response = await templateService.getTemplateAgenzia(agenzia.id);

      if (response.success) {
        setTemplates(response.templates || []);
      }
    } catch (error) {
      console.error('Errore caricamento templates:', error);
      toast.error('Errore nel caricamento dei template');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Sono accettati solo file PDF');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      toast.error('Il file non può superare i 10MB');
      return;
    }

    setFormData({ ...formData, file });

    // Analizza automaticamente i campi del PDF
    await analizzaCampiPdf(file);
  };

  // Nuova funzione per analizzare i campi PDF
  const analizzaCampiPdf = async (file) => {
    try {
      setAnalizzandoPdf(true);
      setCampiPdf([]);
      setShowCampiPreview(false);

      console.log('🔍 Avvio analisi campi PDF...');
      
      const response = await templateService.analizzaCampiPdf(file);
      
      if (response.success) {
        setCampiPdf(response.campi || []);
        setShowCampiPreview(true);
        
        toast.success(
          `Analisi completata! ${response.totaleCampi} campi trovati nel PDF`, 
          { autoClose: 3000 }
        );
        
        console.log(`✅ Analisi completata: ${response.totaleCampi} campi trovati`);
      }
    } catch (error) {
      console.error('❌ Errore analisi campi PDF:', error);
      toast.error(`Errore nell'analisi del PDF: ${error.message}`);
      setCampiPdf([]);
      setShowCampiPreview(false);
    } finally {
      setAnalizzandoPdf(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.nomeTemplate.trim()) {
      toast.error('Il nome del template è obbligatorio');
      return;
    }
    // Il PDF è obbligatorio solo in creazione; in modifica si aggiornano nome e flag primario
    if (!editingTemplate && !formData.file) {
      toast.error('Nome template e file PDF sono obbligatori');
      return;
    }

    try {
      setUploadingFile(true);
      
      if (editingTemplate) {
        // Aggiorna template esistente (solo nome e flag primario)
        const response = await templateService.aggiornaTemplate(
          editingTemplate.id,
          formData.nomeTemplate,
          formData.isPrimario
        );
        
        if (response.success) {
          toast.success('Template aggiornato con successo');
        }
      } else {
        // Crea nuovo template
        const response = await templateService.creaTemplate(
          formData.file,
          formData.nomeTemplate,
          formData.tipoTemplate,
          formData.isPrimario,
          agenzia.id
        );
        
        if (response.success) {
          toast.success('Template creato con successo');
        }
      }
      
      await caricaTemplates();
      resetForm();
    } catch (error) {
      console.error('Errore salvataggio template:', error);
      toast.error(error.response?.data?.error || 'Errore nel salvataggio del template');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo template?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await templateService.eliminaTemplate(templateId);

      if (response.success) {
        toast.success('Template eliminato con successo');
        await caricaTemplates();
      }
    } catch (error) {
      console.error('Errore eliminazione template:', error);
      toast.error(error.response?.data?.error || 'Errore nell\'eliminazione del template');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPrimario = async (templateId) => {
    try {
      setLoading(true);
      const response = await templateService.impostaPrimario(templateId);

      if (response.success) {
        toast.success('Template impostato come primario');
        await caricaTemplates();
      }
    } catch (error) {
      console.error('Errore impostazione primario:', error);
      toast.error('Errore nell\'impostazione del template primario');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (templateId) => {
    try {
      const response = await templateService.downloadTemplate(templateId);
      
      // Crea URL per download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Errore download:', error);
      toast.error('Errore nel download del template');
    }
  };

  const resetForm = () => {
    setFormData({
      nomeTemplate: '',
      tipoTemplate: 'con_intestazione_cliente',
      isPrimario: false,
      file: null
    });
    setEditingTemplate(null);
    setShowForm(false);
    setCampiPdf([]);
    setShowCampiPreview(false);
  };

  const editTemplate = (template) => {
    setFormData({
      nomeTemplate: template.nomeTemplate || '',
      tipoTemplate: template.tipoTemplate || 'con_intestazione_cliente',
      isPrimario: template.isPrimario || false,
      file: null // Non è possibile modificare il file, solo i metadati
    });
    setEditingTemplate(template);
    setShowForm(true);
    setCampiPdf([]);
    setShowCampiPreview(false);
  };

  const previewTemplateData = (template) => {
    setPreviewTemplate(template);
  };

  const getTipoTemplateLabel = (tipo) => {
    return tipo === 'con_intestazione_cliente' 
      ? 'Con intestazione cliente' 
      : 'Senza intestazione cliente';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>
        
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
          {/* Header della Modale */}
          <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-blue-50 to-blue-100">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Gestione Branding & Template</h1>
              <p className="text-sm text-gray-600">
                Gestione per: <strong>{agenzia.nome}</strong>
              </p>
            </div>
            <button
              type="button"
              className="p-2 rounded-full text-gray-400 hover:text-gray-500 hover:bg-white"
              onClick={onClose}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('branding')}
                className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'branding'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <SwatchIcon className="h-5 w-5 inline mr-2" />
                Branding
              </button>
              <button
                onClick={() => setActiveTab('template')}
                className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'template'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <ClipboardDocumentListIcon className="h-5 w-5 inline mr-2" />
                Template Preventivi
              </button>
            </nav>
          </div>

          {/* Content based on active tab */}
          <div className="bg-white">
            {activeTab === 'branding' ? (
              // NUOVO: Sezione Branding
              <div className="p-6 max-h-[calc(90vh-200px)] overflow-y-auto">
                <div className="max-w-2xl mx-auto">
                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Personalizzazione Branding</h3>
                    <p className="text-gray-600 text-sm">
                      Personalizza logo e colore principale per l'agenzia
                    </p>
                  </div>

                  <div className="space-y-8">
                    {/* Upload Logo */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-4">
                        Logo Agenzia
                      </label>
                      
                      {brandingData.logo ? (
                        <div className="border border-gray-300 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-4">
                            <span className="text-sm text-gray-600">Logo attuale:</span>
                                                         <button
                               onClick={rimuoviLogo}
                               disabled={uploadingLogo}
                               className={`text-sm ${
                                 uploadingLogo 
                                   ? 'text-gray-400 cursor-not-allowed' 
                                   : 'text-red-600 hover:text-red-700'
                               }`}
                             >
                              Rimuovi
                            </button>
                          </div>
                          <div className="flex justify-center">
                            <img 
                              src={brandingData.logo} 
                              alt="Logo" 
                              className="max-h-32 max-w-full object-contain border border-gray-200 rounded"
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
                          <PhotoIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoChange}
                            className="hidden"
                            id="logo-upload"
                          />
                          <label 
                            htmlFor="logo-upload" 
                            className="cursor-pointer text-blue-600 hover:text-blue-700 font-medium"
                          >
                            Clicca per caricare logo
                          </label>
                          <p className="text-xs text-gray-500 mt-2">
                            JPG, PNG, GIF, SVG - Massimo 5MB
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Selezione Colore */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-4">
                        Colore Principale
                      </label>
                      <div className="flex items-center space-x-4">
                        <input
                          type="color"
                          value={brandingData.colorePrincipale}
                          onChange={(e) => setBrandingData(prev => ({
                            ...prev,
                            colorePrincipale: e.target.value
                          }))}
                          className="h-12 w-20 border border-gray-300 rounded-md cursor-pointer"
                        />
                        <div className="flex-1">
                          <input
                            type="text"
                            value={brandingData.colorePrincipale}
                            onChange={(e) => setBrandingData(prev => ({
                              ...prev,
                              colorePrincipale: e.target.value
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder="#3B82F6"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Questo colore sarà utilizzato nei preventivi e materiali dell'agenzia
                      </p>
                    </div>

                    {/* Anteprima */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3">Anteprima</h4>
                      <div 
                        className="p-4 rounded-lg text-white"
                        style={{ backgroundColor: brandingData.colorePrincipale }}
                      >
                        <div className="flex items-center space-x-4">
                          {brandingData.logo && (
                            <img 
                              src={brandingData.logo} 
                              alt="Logo" 
                              className="h-8 object-contain bg-white rounded px-2 py-1"
                            />
                          )}
                          <div>
                            <h3 className="font-bold">{agenzia.nome}</h3>
                            <p className="text-sm opacity-90">Esempio di utilizzo del branding</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Pulsanti azione */}
                    <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                      <button
                        onClick={() => caricaBranding()}
                        className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Ripristina
                      </button>
                      <button
                        onClick={salvaBranding}
                        disabled={uploadingLogo}
                        className={`px-6 py-2 border border-transparent rounded-md text-sm font-medium text-white ${
                          uploadingLogo
                            ? 'bg-gray-300 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                      >
                        {uploadingLogo && (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                        )}
                        Salva Branding
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Sezione Template Preventivi (codice esistente)
              <div className="flex h-full max-h-[calc(90vh-200px)]">
                {/* Sidebar Lista Template */}
                <div className="w-1/3 border-r border-gray-200 overflow-y-auto">
                  <div className="p-4 border-b border-gray-200">
                    <button
                      onClick={() => {
                        resetForm();
                        setShowForm(true);
                      }}
                      className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
                    >
                      <PlusIcon className="h-5 w-5" />
                      <span>Nuovo Template</span>
                    </button>
                  </div>

                  <div className="p-4">
                    {loading ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="text-gray-600 mt-2 text-sm">Caricamento...</p>
                      </div>
                    ) : templates.length === 0 ? (
                      <div className="text-center py-8">
                        <ClipboardDocumentListIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600 text-sm">Nessun template trovato</p>
                        <p className="text-xs text-gray-500 mt-1">Crea il primo template per questa agenzia</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {templates.map((template) => (
                          <div
                            key={template.id}
                            className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                              previewTemplate?.id === template.id
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => previewTemplateData(template)}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="font-bold text-gray-900 text-sm flex items-center">
                                {template.isPrimario && (
                                  <StarSolidIcon className="h-4 w-4 text-yellow-500 mr-1" />
                                )}
                                {template.nomeTemplate}
                              </h3>
                            </div>
                            
                            <p className="text-xs text-gray-600 mb-3">
                              {getTipoTemplateLabel(template.tipoTemplate)}
                            </p>

                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-1">
                                {template.isPrimario ? (
                                  <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                                    Primario
                                  </span>
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSetPrimario(template.id);
                                    }}
                                    className="text-xs text-gray-500 hover:text-yellow-600 transition-colors"
                                    title="Imposta come primario"
                                  >
                                    <StarIcon className="h-4 w-4" />
                                  </button>
                                )}
                              </div>

                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownload(template.id);
                                  }}
                                  className="text-green-600 hover:text-green-700 transition-colors"
                                  title="Download PDF"
                                >
                                  <DocumentIcon className="h-4 w-4" />
                                </button>
                                
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditorTemplate(template);
                                  }}
                                  className="text-purple-600 hover:text-purple-700 transition-colors"
                                  title="Posiziona campi"
                                >
                                  <Squares2X2Icon className="h-4 w-4" />
                                </button>

                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    editTemplate(template);
                                  }}
                                  className="text-blue-600 hover:text-blue-700 transition-colors"
                                  title="Modifica"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                                
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteTemplate(template.id);
                                  }}
                                  className="text-red-600 hover:text-red-700 transition-colors"
                                  title="Elimina"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Contenuto principale */}
                <div className="flex-1 overflow-y-auto">
                  {showForm ? (
                    // Form Template
                    <div className="p-6">
                      <div className="mb-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                          {editingTemplate ? 'Modifica Template' : 'Nuovo Template'}
                        </h3>
                        <p className="text-gray-600 text-sm">
                          {editingTemplate 
                            ? 'Modifica nome e impostazioni del template'
                            : 'Carica un nuovo template PDF per l\'agenzia'
                          }
                        </p>
                      </div>

                      <div className="space-y-6">
                        {/* Nome Template */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Nome Template <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.nomeTemplate}
                            onChange={(e) => setFormData({...formData, nomeTemplate: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                            placeholder="Es: Template Brumly Standard"
                            required
                          />
                        </div>

                        {/* Tipo Template (solo per nuovi) */}
                        {!editingTemplate && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Tipo Template <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={formData.tipoTemplate}
                              onChange={(e) => setFormData({...formData, tipoTemplate: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                            >
                              <option value="con_intestazione_cliente">Con intestazione cliente</option>
                              <option value="senza_intestazione_cliente">Senza intestazione cliente</option>
                            </select>
                          </div>
                        )}

                        {/* Upload PDF (solo per nuovi) */}
                        {!editingTemplate && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              File PDF Template <span className="text-red-500">*</span>
                            </label>
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                              <DocumentArrowUpIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                              <input
                                type="file"
                                accept=".pdf"
                                onChange={handleFileChange}
                                className="hidden"
                                id="pdf-upload"
                              />
                              <label 
                                htmlFor="pdf-upload" 
                                className="cursor-pointer text-blue-600 hover:text-blue-700 font-medium"
                              >
                                {formData.file ? formData.file.name : 'Clicca per caricare PDF'}
                              </label>
                              <p className="text-xs text-gray-500 mt-1">
                                Solo file PDF, massimo 10MB
                              </p>
                              
                              {/* Indicatore di analisi */}
                              {analizzandoPdf && (
                                <div className="mt-4 flex items-center justify-center text-blue-600">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                                  <span className="text-sm">Analisi campi PDF in corso...</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Anteprima campi PDF */}
                        {showCampiPreview && campiPdf.length > 0 && (
                          <div className="bg-green-50 rounded-lg p-4">
                            <div className="flex items-center mb-3">
                              <DocumentMagnifyingGlassIcon className="h-5 w-5 text-green-600 mr-2" />
                              <h4 className="font-medium text-green-800">
                                Campi PDF Rilevati ({campiPdf.length})
                              </h4>
                            </div>
                            
                            <div className="max-h-32 overflow-y-auto">
                              <div className="grid grid-cols-1 gap-2 text-sm">
                                {campiPdf.map((campo, index) => (
                                  <div key={index} className="flex items-center justify-between bg-white px-3 py-2 rounded border">
                                    <div className="flex items-center">
                                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded mr-3">
                                        {campo.indice}
                                      </span>
                                      <span className="font-medium text-gray-800">
                                        {campo.nome}
                                      </span>
                                    </div>
                                    <span className="text-gray-500 text-xs">
                                      {campo.tipoSemplificato}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            
                            <div className="mt-3 text-xs text-green-700">
                              ✅ Il PDF contiene campi compilabili e può essere utilizzato come template per i preventivi.
                            </div>
                          </div>
                        )}

                        {/* Messaggio se non ci sono campi */}
                        {showCampiPreview && campiPdf.length === 0 && (
                          <div className="bg-yellow-50 rounded-lg p-4">
                            <div className="flex items-center mb-2">
                              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2" />
                              <h4 className="font-medium text-yellow-800">
                                Nessun campo compilabile trovato
                              </h4>
                            </div>
                            <p className="text-yellow-700 text-sm">
                              Il PDF caricato non contiene campi compilabili. Assicurati che il PDF sia stato creato con campi form per poter essere utilizzato come template per i preventivi.
                            </p>
                          </div>
                        )}

                        {/* Flag Primario */}
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.isPrimario}
                            onChange={(e) => setFormData({...formData, isPrimario: e.target.checked})}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label className="ml-2 text-sm text-gray-700">
                            Imposta come template primario per questo tipo
                          </label>
                        </div>
                      </div>

                      {/* Footer Form */}
                      <div className="mt-6 pt-6 border-t border-gray-200 flex justify-end space-x-3">
                        <button
                          onClick={resetForm}
                          className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Annulla
                        </button>
                        <button
                          onClick={handleSubmit}
                          disabled={uploadingFile || !formData.nomeTemplate.trim() || (!editingTemplate && !formData.file)}
                          className={`px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white ${
                            uploadingFile || !formData.nomeTemplate.trim() || (!editingTemplate && !formData.file)
                              ? 'bg-gray-300 cursor-not-allowed'
                              : 'bg-blue-600 hover:bg-blue-700'
                          }`}
                        >
                          {uploadingFile && (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                          )}
                          {editingTemplate ? 'Aggiorna' : 'Crea'} Template
                        </button>
                      </div>
                    </div>
                  ) : previewTemplate ? (
                    // Preview Template
                    <div className="p-6">
                      <div className="mb-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                          {previewTemplate.isPrimario && (
                            <StarSolidIcon className="h-5 w-5 text-yellow-500 mr-2" />
                          )}
                          {previewTemplate.nomeTemplate}
                        </h3>
                        <p className="text-gray-600 text-sm">
                          {getTipoTemplateLabel(previewTemplate.tipoTemplate)}
                        </p>
                      </div>

                      <div className="space-y-4">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="font-medium text-gray-900 mb-3">Dettagli Template</h4>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-gray-500">Tipo</p>
                              <p className="font-medium">{getTipoTemplateLabel(previewTemplate.tipoTemplate)}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Stato</p>
                              <span className={previewTemplate.isPrimario ? 'text-yellow-600' : 'text-gray-600'}>
                                {previewTemplate.isPrimario ? 'Primario' : 'Standard'}
                              </span>
                            </div>
                            <div>
                              <p className="text-gray-500">File</p>
                              <p className="font-medium">{previewTemplate.nomeFileOriginale}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Creato</p>
                              <p className="font-medium">{new Date(previewTemplate.dataCreazione).toLocaleDateString('it-IT')}</p>
                            </div>
                          </div>
                        </div>

                        <div className="flex space-x-3">
                          <button
                            onClick={() => handleDownload(previewTemplate.id)}
                            className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
                          >
                            <DocumentIcon className="h-4 w-4" />
                            <span>Download PDF</span>
                          </button>
                          
                          <button
                            onClick={() => editTemplate(previewTemplate)}
                            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                          >
                            <PencilIcon className="h-4 w-4" />
                            <span>Modifica</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Stato iniziale
                    <div className="flex items-center justify-center h-full p-6">
                      <div className="text-center">
                        <ClipboardDocumentListIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                          Gestione Template Preventivi
                        </h3>
                        <p className="text-gray-600 mb-6 max-w-md text-sm">
                          Carica i template PDF per i preventivi. Puoi avere template 
                          con o senza intestazione cliente.
                        </p>
                        <button
                          onClick={() => {
                            resetForm();
                            setShowForm(true);
                          }}
                          className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors flex items-center space-x-2 mx-auto"
                        >
                          <PlusIcon className="h-5 w-5" />
                          <span>Carica Primo Template</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {editorTemplate && (
        <EditorCampiTemplate
          template={editorTemplate}
          onClose={() => setEditorTemplate(null)}
        />
      )}
    </div>
  );
};

export default GestoreTemplatePreventivi; 